import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import {
  concat,
  concatMap,
  defaultIfEmpty,
  defer,
  EMPTY,
  map,
  Observable,
  of,
  switchMap,
} from 'rxjs';

import { Option } from '@sapphire/result';

import { EffectsBase } from '@ps/core-data-access';
import { AuthSelectorsFacade } from '@ps/auth-data-access';
import { APP_ROUTES } from '@ps/core-config';
import { ToastService } from '@ps/core-overlay';
import { SURVEY_STATUS } from '@ps/survey-kernel';
import {
  MC_GROUPED_MESSAGES,
  Survey,
  SurveyKind,
  trafficChannelParentId,
} from '@ps/survey-utils';

import { SurveyCreationInsightsActions } from '../insights';

import {
  SurveyCreationCoreActions,
  SurveyCreationCoreApiActions,
} from './survey-creation-core.actions';
import {
  SurveyCreationLaunchActions,
  SurveyCreationLaunchApiActions,
} from '../launch/survey-creation-launch.actions';
import {
  SurveyCreationAudienceApiActions,
  SurveyCreationMcApiActions,
} from '../audience/survey-creation-audience.actions';
import {
  DEFAULT_SAVE_GATE,
  SURVEY_NOT_READY_TO_LAUNCH_MESSAGE,
} from './utils/survey-save-pipeline.helper';
import { SurveyPersistenceService } from '../../services/save/survey-persistence.service';
import { SurveySavePipeline } from './survey-save.pipeline';

// Owns the save/clone lifecycle for a standard survey — save is dispatched
// from create, edit and launch alike, so it lives in core/ (unlike creation,
// which is create-only and lives in SurveyAudienceCreateEffects in audience/;
// both share the stage-0 read via survey-save-pipeline.helper.ts).
@Injectable()
export class SurveyCreationSaveEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);

  private readonly _toastService = inject(ToastService);
  private readonly _router = inject(Router);

  private readonly _persistence = inject(SurveyPersistenceService);
  private readonly _authSelectors = inject(AuthSelectorsFacade);
  private readonly _savePipeline = inject(SurveySavePipeline);

  // Only a plain save stays on the page, so only it refreshes insights — and
  // only when the caller asked for it. The launch page never displays
  // feasibility/score/supplier data, so it saves with recalculationsEnabled:
  // false rather than pay for a refresh nothing reads.
  public readonly saveStandardSurvey$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreActions.saveSurvey),
      this._savePipeline.saveWithTrigger(
        (action) => () =>
          !action.recalculationsEnabled
            ? EMPTY
            : of(SurveyCreationInsightsActions.refreshAfterSave()),
      ),
    ),
  );

  public readonly saveAndCreateNewSurvey$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreActions.saveAndCreateNew),
      this._savePipeline.save(() => this._navigateTo(APP_ROUTES.CREATE_SURVEY)),
    ),
  );

  public readonly saveSurveyAndNavigateToLaunchPage$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreActions.saveAndLaunch),
      this._savePipeline.save((survey) =>
        this._navigateTo(
          APP_ROUTES.launchSurvey(this._routableSurveyId(survey)),
        ),
      ),
    ),
  );

  // A Specific Audience channel goes live in the same write as its audience,
  // then the buyer is taken back to the survey it was launched from.
  public readonly saveSurveyAsLive$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreActions.saveAsLive),
      this._savePipeline.save(
        (survey) =>
          this._navigateTo(
            trafficChannelParentId(survey).mapOr(
              APP_ROUTES.DASHBOARD,
              APP_ROUTES.manageSurvey,
            ),
          ),
        DEFAULT_SAVE_GATE,
        { status: SURVEY_STATUS.LIVE },
      ),
    ),
  );

  public readonly saveAndEditAudienceSurvey$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreActions.saveAndEditAudience),
      this._savePipeline.save((survey) =>
        this._navigateTo(APP_ROUTES.editSurvey(this._routableSurveyId(survey))),
      ),
    ),
  );

  // Redirect trigger is scoped to this action alone, not bare saveSurveySuccess —
  // every other save variant shares that action, so wiring off it directly would
  // make the ordinary page Save button also pop a test-link tab.
  public readonly saveAndTestSurvey$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreActions.saveAndTest),
      this._savePipeline.save(() =>
        of(SurveyCreationLaunchApiActions.testEntryLinkReady()),
      ),
    ),
  );

  public readonly saveAndCloneSurvey$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreActions.saveAndCloneSurvey),
      this._savePipeline.save((survey) => this._cloneSurvey(survey)),
    ),
  );

  // Saves, then hands off to SurveyCreationLaunchSubmitEffects for the status
  // change. The save gate drops an invalid survey without emitting (its toasts
  // already say why), so defaultIfEmpty ends the launch attempt explicitly.
  public readonly launchSurvey$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationLaunchActions.launchSurvey),
      concatMap((action) =>
        of<Action>(action).pipe(
          this._savePipeline.save(
            (survey) =>
              of(
                SurveyCreationLaunchApiActions.changeLaunchStatus({
                  surveyId: survey.id,
                  status: action.status,
                  pricingConfirmationMessage: action.pricingConfirmationMessage,
                }),
              ),
            { alwaysGateLaunchForm: true },
          ),
          defaultIfEmpty<Action, Action>(
            SurveyCreationLaunchApiActions.launchSurveyFailure({
              error: SURVEY_NOT_READY_TO_LAUNCH_MESSAGE,
            }),
          ),
        ),
      ),
    ),
  );

  // Only announces the save succeeded — SurveyCreationWorkflowExitEffects owns
  // the navigate, same split as the impersonation-exit variants below.
  public readonly saveAndExitSurvey$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreActions.saveAndExit),
      this._savePipeline.save((survey) =>
        of(SurveyCreationCoreApiActions.saveSurveyAndExitSuccess({ survey })),
      ),
    ),
  );

  // Only announces the save succeeded — SurveyCreationWorkflowExitEffects owns
  // what happens next (disconnecting, navigating). Keeps this class's one job
  // "run the save pipeline" regardless of why a caller wanted the exit.
  public readonly saveSurveyAndDisconnectImpersonation$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreActions.saveSurveyAndDisconnectImpersonation),
      this._savePipeline.save((survey) =>
        of(
          SurveyCreationCoreApiActions.saveSurveyAndDisconnectImpersonationSuccess(
            {
              survey,
            },
          ),
        ),
      ),
    ),
  );

  // Same reasoning as above; the target buyer isn't carried through the
  // pipeline's own stages, so it's closed over per-emission here instead.
  public readonly saveSurveyAndSwitchImpersonatedBuyer$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreActions.saveSurveyAndSwitchImpersonatedBuyer),
      this._savePipeline.saveWithTrigger(
        (action) => (survey) =>
          of(
            SurveyCreationCoreApiActions.saveSurveyAndSwitchImpersonatedBuyerSuccess(
              { survey, buyer: action.buyer },
            ),
          ),
      ),
    ),
  );

  // Same reasoning as above; routeKey/params aren't carried through the
  // pipeline's own stages, so they're closed over per-emission here instead.
  // Only announces the save succeeded — SurveyCreationWorkflowExitEffects owns
  // performing the actual switch, same split as the other save-and-X variants.
  public readonly saveSurveyAndSwitchToLegacy$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreActions.saveSurveyAndSwitchToLegacy),
      concatMap(({ routeKey, params }) =>
        of<Action>(
          SurveyCreationCoreActions.saveSurveyAndSwitchToLegacy({
            routeKey,
            params,
          }),
        ).pipe(
          this._savePipeline.save((survey) =>
            of(
              SurveyCreationCoreApiActions.saveSurveyAndSwitchToLegacySuccess({
                survey,
                routeKey,
                params,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  // Reset before navigating so the edit page loads fresh data, not stale
  // state. concat + defer guarantees that order — tap-then-map would only
  // work by accident of Router deferring navigation to a microtask.
  public readonly navigateToEditSurvey$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        SurveyCreationAudienceApiActions.createStandardSurveySuccess,
        SurveyCreationCoreApiActions.cloneSurveySuccess,
        SurveyCreationAudienceApiActions.createSurveyFromTemplateSuccess,
        SurveyCreationMcApiActions.createMcTraditionalSurveysSuccess,
        SurveyCreationMcApiActions.createMcGroupedProjectSuccess,
      ),
      switchMap(({ survey }) =>
        concat(
          of(SurveyCreationCoreActions.reset()),
          defer(() => this._navigateTo(APP_ROUTES.editSurvey(survey.id))),
        ),
      ),
    ),
  );

  // Emits cloneSurveySuccess/Failure so navigateToEditSurvey$ (and any other listener) reacts
  // the same way regardless of the caller.
  //
  // A Group MC has already been persisted by the time this runs — only the
  // clone itself is unsupported, so it reports that and contributes no action
  // rather than failing the save the buyer just completed.
  private _cloneSurvey(survey: Survey): Observable<Action> {
    if (SurveyKind.isGroupMc(survey)) {
      this._toastService.info(MC_GROUPED_MESSAGES.CLONE_UNAVAILABLE);
      return EMPTY;
    }

    return this._persistence
      .clone(survey.id, this._authSelectors.hasImpersonatedBuyer())
      .pipe(
        map((clonedSurvey) => {
          this._toastService.success('Survey successfully cloned', {
            keepOpen: false,
          });
          return SurveyCreationCoreApiActions.cloneSurveySuccess({
            survey: clonedSurvey,
          });
        }),
        this.psCatchError(
          (error) =>
            of(
              SurveyCreationCoreApiActions.cloneSurveyFailure({
                error: error.message,
              }),
            ),
          { notify: true },
        ),
      );
  }

  // Navigates as a side effect and contributes no action to the stream.
  private _navigateTo(route: string): Observable<never> {
    this._router.navigate([route]);
    return EMPTY;
  }

  // A project-backed survey is routed by its project id, not the child
  // survey's own id.
  private _routableSurveyId(survey: Survey): number {
    return SurveyKind.isProject(survey)
      ? Option.from(survey.projectId).unwrapOr(survey.id)
      : survey.id;
  }
}
